import type { Platform } from './platform.js';
import type { SourceType } from './source.js';
import type { TenantId } from './tenant.js';

export type DistributionStatus = 'queued' | 'running' | 'succeeded' | 'partial' | 'failed';

export type DistributionItemStatus = 'pending' | 'staged' | 'rejected' | 'uploaded' | 'failed';

/** One user request: send N layouts of one source to one target. */
export interface Distribution {
  id: string;
  tenantId: TenantId;
  sourceType: SourceType;
  sourceId: string;
  /** Snapshot of the source name at request time; Kadanza may rename it later. */
  sourceName: string;
  targetId: string;
  platform: Platform;
  status: DistributionStatus;
  requestedByUserId: string;
  error: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

/** One layout within a distribution. Carries `tenantId` so every query stays tenant-leading. */
export interface DistributionItem {
  id: string;
  tenantId: TenantId;
  distributionId: string;
  layoutId: string;
  layoutName: string;
  width: number | null;
  height: number | null;
  /** Snapshotted at request time — never re-resolved from Kadanza. */
  contentUrl: string;
  r2Key: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  /**
   * Platform resource name of the created asset. Google Ads assets are
   * immutable, so this records the result of one attempt rather than a stable
   * mapping.
   */
  remoteAssetId: string | null;
  status: DistributionItemStatus;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Queue message handed to the workflow runner. Carries the tenant so nothing re-derives it. */
export interface DistributionRunMessage {
  tenantId: TenantId;
  distributionId: string;
}

export const TERMINAL_DISTRIBUTION_STATUSES: readonly DistributionStatus[] = [
  'succeeded',
  'partial',
  'failed',
];

export function isTerminalDistributionStatus(status: DistributionStatus): boolean {
  return TERMINAL_DISTRIBUTION_STATUSES.includes(status);
}

/** Roll up item outcomes into the parent status. */
export function summarizeItemStatuses(
  statuses: readonly DistributionItemStatus[],
): DistributionStatus {
  if (statuses.length === 0) return 'failed';
  const uploaded = statuses.filter((s) => s === 'uploaded').length;
  const settled = statuses.filter(
    (s) => s === 'uploaded' || s === 'failed' || s === 'rejected',
  ).length;
  if (settled < statuses.length) return 'running';
  if (uploaded === statuses.length) return 'succeeded';
  if (uploaded === 0) return 'failed';
  return 'partial';
}
